import { type ComponentType, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Art } from '../components/Art.js';
import { Icon } from '../components/Icon.js';
import { useConfirm } from '../components/Modal.js';
import { useOpenSearch } from '../features/panduan/search.js';
import { greet, initials } from '../lib/format.js';
import { isLazyView } from './lazyView.js';
import { BOTTOM, bottomLabel, HIDDEN_VIEWS, homeFor, type NavItem, navFor, PROMO, SECTION, TITLES } from './nav.js';
import { useLogout, useSession } from './session.js';
import { Loading, normTo, type Route, ViewContext } from './view.js';

// Kerangka aplikasi setelah login — DOM sama dengan #app.shell v1 (index.html + drawNav/renderNow di admin.js & core.js).

export type ViewComponent = ComponentType;

/** "/pesanan?tab=aktif" atau "#/pesanan?tab=aktif" → { view, param, q }. */
function parseRoute(pathname: string, search: string): Route {
  const [view = '', param = ''] = pathname.replace(/^\/+/, '').split('/');
  return { view, param: param ? decodeURIComponent(param) : '', q: Object.fromEntries(new URLSearchParams(search)) };
}

export function Shell({ views }: { views: Record<string, ViewComponent> }) {
  const { me, meta, counts, isMgr, can } = useSession();
  const logout = useLogout(useConfirm());
  const openSearch = useOpenSearch();
  const loc = useLocation();
  const navigate = useNavigate();
  const r = useMemo(() => parseRoute(loc.pathname, loc.search), [loc.pathname, loc.search]);
  // Menu & halaman awal mengikuti hak peran (peran kustom diatur owner).
  const navList = useMemo(() => navFor(me.role, can), [me.role, can]);
  const home = homeFor(me.role, navList);
  const allowed = navList.map((n) => n[0]).concat(HIDDEN_VIEWS);
  const [renderKey, setRenderKey] = useState(0);
  // Jumlah perpindahan halaman di dalam aplikasi (untuk tombol kembali).
  const depth = useRef(-1);
  const prevLoc = useRef('');

  const go = useCallback(
    (to: string) => {
      const t = normTo(to);
      if (t === loc.pathname + loc.search) setRenderKey((k) => k + 1);
      else navigate(t);
    },
    [loc.pathname, loc.search, navigate],
  );
  const reload = useCallback(() => setRenderKey((k) => k + 1), []);

  const isHome = r.view === home;
  // Atribut <body> dipakai CSS v1 (body.is-home …).
  useEffect(() => {
    document.body.dataset.view = r.view;
    document.body.classList.toggle('is-home', isHome);
    window.scrollTo(0, 0);
    const key = loc.pathname + loc.search;
    if (key !== prevLoc.current) {
      depth.current += 1;
      prevLoc.current = key;
    }
  }, [r.view, isHome, loc.pathname, loc.search]);
  useEffect(() => {
    document.title = meta.settings.business_name ? `${meta.settings.business_name} — Penjualan` : 'Penjualan';
  }, [meta.settings.business_name]);

  // Muat kode semua layar peran ini di latar belakang setelah login, agar pindah menu tidak menunggu unduhan.
  useEffect(() => {
    const roleViews = navList.map((n) => n[0]).concat(HIDDEN_VIEWS);
    const run = () => {
      for (const v of roleViews) {
        const c = views[v];
        if (isLazyView(c)) void c.preload().catch(() => {});
      }
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 800);
  }, [navList, views]);

  // Delegasi klik global [data-go] (v1 core.js) — kecuali di dalam modal (modal memakai onClick sendiri).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || t.closest('#modalRoot')) return;
      const g = t.closest<HTMLElement>('[data-go]');
      if (g?.dataset.go) {
        e.preventDefault();
        go(g.dataset.go);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [go]);

  if (!r.view || !allowed.includes(r.view) || !views[r.view]) return <Navigate replace to={`/${home}`} />;
  const View = views[r.view] as ViewComponent;

  const back = () => {
    if (depth.current > 0) {
      depth.current -= 2;
      window.history.back();
      return;
    }
    go(Object.keys(r.q).length && navList.some((n) => n[0] === r.view) ? `/${r.view}` : `/${home}`);
  };

  const badge = (k: string) =>
    k === 'spo' && counts.spo
      ? counts.spo
      : k === 'nota' && counts.nota
        ? counts.nota
        : k === 'keuangan' && counts.keu
          ? counts.keu
          : (k === 'dks' || k === 'kunjungan') && counts.kunj
            ? counts.kunj
            : k === 'toko-home' && counts.konf
              ? counts.konf
              : 0;
  const total = (counts.spo || 0) + (counts.nota || 0) + (counts.keu || 0) + (counts.kunj || 0) + (counts.konf || 0);
  const has = (v: string) => navList.some((n) => n[0] === v);
  const bellGo = isMgr
    ? counts.spo
      ? '/spo'
      : counts.keu
        ? '/keuangan?tab=transfer'
        : !counts.nota && counts.kunj
          ? '/kunjungan?cek=1'
          : has('nota')
            ? '/nota'
            : `/${home}`
    : me.role === 'sales' && counts.kunj
      ? '/dks'
      : me.role === 'toko' && counts.konf
        ? '/toko-home'
        : ({ sales: '/spo?tab=ditolak', toko: '/pesanan' } as Record<string, string>)[me.role] || '/tugas';
  const promo = PROMO[me.role];
  const all: Record<string, NavItem> = Object.fromEntries([
    ...navList.map((n) => [n[0], n] as const),
    ['more', ['menu', 'Lainnya', 'more'] as NavItem],
    ['akun', ['akun', 'Profil', 'user'] as NavItem],
  ]);
  const active = (v: string) => (r.view === v ? ' active' : '');

  let navCur = '';
  const navItems = navList.flatMap(([k, l, i]) => {
    const sec = SECTION[k] || '';
    const head =
      sec && sec !== navCur ? (
        <div className="nav-sec" key={`s-${sec}`}>
          {sec}
        </div>
      ) : null;
    navCur = sec || navCur;
    return [
      head,
      <button key={k} type="button" className={`nav-item${active(k)}`} data-go={`/${k}`}>
        <Icon n={i} />
        <span>{l}</span>
        {badge(k) ? <span className="count">{badge(k)}</span> : null}
      </button>,
    ];
  });

  return (
    <div id="app" className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="logo-mark" id="brandLogo">
            <Icon n="bag" />
          </div>
          <div>
            <b id="brandName">{meta.settings.business_name}</b>
          </div>
        </div>
        <nav className="nav" id="nav" aria-label="Menu utama">
          {navItems}
        </nav>
        <div className={`side-promo${promo ? '' : ' hidden'}`} id="sidePromo">
          {promo ? (
            <>
              <b>{promo[0]}</b>
              <span>{promo[1]}</span>
              <button type="button" className="btn btn-primary btn-sm" data-go={promo[2]}>
                {promo[3]}
              </button>
              <Art name="" id={0} cls="promo-art" />
            </>
          ) : null}
        </div>
        <div className="side-user" id="sideUser">
          <div className="avatar">{initials(me.name)}</div>
          <div className="who">
            <b>{me.name}</b>
            <span>{me.role_name || meta.roles[me.role]}</span>
          </div>
          <button type="button" className="icon-btn" data-go="/akun" title="Profil">
            <Icon n="user" cls="i-sm" />
          </button>
          <button type="button" className="icon-btn" title="Keluar" data-act="logout" onClick={() => void logout()}>
            <Icon n="logout" cls="i-sm" />
          </button>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <button
            className={`icon-btn back${isHome ? ' hidden' : ''}`}
            id="backBtn"
            type="button"
            aria-label="Kembali"
            onClick={back}
          >
            <Icon n="back" />
          </button>
          <div className={`tb-hello${isHome ? '' : ' hidden'}`} id="tbHello">
            {isHome ? (
              <>
                <span className="pin">
                  <Icon n="pin" />
                </span>
                <div>
                  <small>{greet()},</small>
                  <b>{me.name}</b>
                  <em>
                    {meta.store
                      ? `Kirim ke ${meta.store.name}`
                      : `${meta.settings.business_name} · ${me.role_name || meta.roles[me.role]}`}
                  </em>
                </div>
              </>
            ) : null}
          </div>
          <h1 id="pageTitle">{TITLES[r.view] || ''}</h1>
          <span id="topActions" className="row" />
          <button
            className="icon-btn"
            id="searchBtn"
            type="button"
            aria-label="Cari (Ctrl+K)"
            title="Cari (Ctrl+K)"
            onClick={openSearch}
          >
            <Icon n="search" />
          </button>
          <button className="icon-btn bell" id="bellBtn" type="button" aria-label="Notifikasi" data-go={bellGo}>
            <Icon n="bell" />
            {total ? <span className="dot">{total > 99 ? '99+' : total}</span> : null}
          </button>
          <button className="tb-avatar" id="tbAvatar" type="button" data-go="/akun" aria-label="Akun">
            {initials(me.name)}
          </button>
        </header>
        <section id="view" className="view">
          <ViewContext.Provider value={{ r, go, reload }}>
            {/* Batas Suspense per halaman (di dalam #view): saat kode layar baru diunduh, menu langsung aktif &
                konten menampilkan "Memuat…" seperti v1 — bukan halaman lama yang diam menunggu. */}
            <Suspense key={`${loc.pathname}${loc.search}#${renderKey}`} fallback={<Loading />}>
              {/* Password sementara belum diganti: halaman belum dimuat (API ditolak 428) — modal ganti password di atasnya. */}
              {me.must_change_pw ? <Loading /> : <View />}
            </Suspense>
          </ViewContext.Provider>
        </section>
      </main>
      <nav className="bottom-nav" id="bottomNav" aria-label="Menu">
        {BOTTOM[me.role]
          .filter((k) => k === 'more' || k === 'akun' || has(k))
          .map((k) => {
            const [v, l, i] = all[k] as NavItem;
            const fab = v === 'spo-baru' || v === 'pesan' ? 'fab' : '';
            const cls = [fab, r.view === v ? 'active' : ''].filter(Boolean).join(' ');
            return (
              <button key={k} type="button" data-go={`/${v}`} className={cls || undefined}>
                <Icon n={i} />
                <span>{bottomLabel(v, l, me.role)}</span>
                {badge(k) ? <span className="count">{badge(k)}</span> : null}
              </button>
            );
          })}
      </nav>
    </div>
  );
}
